import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInput } from 'src/components/molecules/FormInput';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';
import { useCreateResetMutation } from 'src/graphql';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { FormInputVariant, FormState } from 'src/types';
import { isEmail } from 'validator';
import {
  ButtonContainer,
  FormContainer,
  InputsContainer,
} from '../../ReportingLogin/ReportingLogin.styles';
import { LoginContainer } from '../../ReportingLoginContainer/ReportingLoginContainer';
import { Description, HeaderWrapper } from '../ReportingForgotPassword.styles';
import { ReportingSendEmailResult } from '../ReportingSendEmailResult/ReportingSendEmailResult';

interface FormValues {
  email: string;
}

export const ReportingSendResetPasswordEmail = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    register,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
  });

  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const getResetToken = useCreateResetMutation({
    onSettled() {
      setHasSubmitted(true);
    },
  });

  const onSubmit = async (data: FormValues) => {
    const { email } = data;
    setEmail(email);

    getResetToken.mutate({
      email,
    });
  };

  return (
    <>
      {hasSubmitted ? (
        <ReportingSendEmailResult
          isSuccess={getResetToken.isSuccess}
          email={email}
        />
      ) : (
        <LoginContainer>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <InputsContainer>
              <HeaderWrapper data-testid="reporting-send-reset-password-email-header">
                {t('reporting.ReportingForgotPassword.heading')}
              </HeaderWrapper>
              <Description data-testid="reporting-send-reset-password-email-description">
                {t('reporting.ReportingForgotPassword.description')}
              </Description>
              <FormInput
                variant={FormInputVariant.INPUT}
                state={FormState.DEFAULT}
                label={t('reporting.ReportingLogin.emailLabel')}
                placeholder={'email@address.com'}
                type="text"
                {...register('email', {
                  required: true,
                  validate: (value) => isEmail(value),
                })}
              />
            </InputsContainer>

            <ButtonContainer isMobile={isMobile}>
              <SpinnerButton
                variant="primary"
                size="medium"
                type="submit"
                width="100%"
                disabled={!isDirty || !isValid}
              >
                {t('reporting.ReportingForgotPassword.sendLink')}
              </SpinnerButton>
            </ButtonContainer>
          </FormContainer>
        </LoginContainer>
      )}
    </>
  );
};
